/**
 * WHAT: Hooks for scheduling one-shot delayed callbacks with automatic cleanup
 *       (useSafeTimeout, useAutoTimeout).
 * WHY:  DAMA components use timeouts for auto-dismissing success/error banners,
 *       debouncing search inputs, and delayed state transitions. Native
 *       setTimeout leaks if the component unmounts before the timer fires;
 *       these hooks track all pending timeouts and clear them on unmount.
 */

import { useEffect, useRef, useCallback } from 'react';

/**
 * WHAT: Returns setSafeTimeout / clearSafeTimeout / clearAllTimeouts functions
 *       that manage a Set of pending timeout IDs and clear them on unmount.
 * WHY:  Components may schedule multiple overlapping timeouts (e.g., several
 *       toast messages). This hook tracks every ID and guarantees none fire
 *       after the component is removed from the tree.
 *
 * useSafeTimeout - A hook that provides a safe setTimeout that automatically
 * cleans up when the component unmounts.
 *
 * @returns {Object} An object containing:
 *   - setSafeTimeout: Function to set a timeout that auto-cleans on unmount
 *   - clearSafeTimeout: Function to manually clear a specific timeout
 *   - clearAllTimeouts: Function to clear all active timeouts
 *
 * @example
 * const { setSafeTimeout, clearSafeTimeout } = useSafeTimeout();
 *
 * // Set a timeout (returns timeout ID)
 * const timeoutId = setSafeTimeout(() => {
 *   setMessage(null);
 * }, 5000);
 *
 * // Optionally clear it early
 * clearSafeTimeout(timeoutId);
 *
 * // Or clear all timeouts
 * clearAllTimeouts();
 */
export function useSafeTimeout() {
  const timeoutsRef = useRef(new Set());
  const isMountedRef = useRef(true);

  // Cleanup all timeouts on unmount
  useEffect(() => {
    isMountedRef.current = true;
    const timeouts = timeoutsRef.current;

    return () => {
      isMountedRef.current = false;
      timeouts.forEach(timeoutId => {
        clearTimeout(timeoutId);
      });
      timeouts.clear();
    };
  }, []);

  const setSafeTimeout = useCallback((callback, delay) => {
    const timeoutId = setTimeout(() => {
      timeoutsRef.current.delete(timeoutId);
      if (isMountedRef.current) {
        callback();
      }
    }, delay);

    timeoutsRef.current.add(timeoutId);
    return timeoutId;
  }, []);

  const clearSafeTimeout = useCallback((timeoutId) => {
    if (timeoutsRef.current.has(timeoutId)) {
      clearTimeout(timeoutId);
      timeoutsRef.current.delete(timeoutId);
    }
  }, []);

  const clearAllTimeouts = useCallback(() => {
    timeoutsRef.current.forEach(timeoutId => {
      clearTimeout(timeoutId);
    });
    timeoutsRef.current.clear();
  }, []);

  return { setSafeTimeout, clearSafeTimeout, clearAllTimeouts };
}

/**
 * WHAT: Automatically starts a timeout whenever a trigger value becomes truthy,
 *       and clears the previous timeout if the trigger changes again.
 * WHY:  The most common timeout pattern in DAMA is "show a message, then hide
 *       it after N seconds." This hook turns that into a one-liner:
 *       `useAutoTimeout(() => setMsg(null), 5000, msg)`.
 *
 * useAutoTimeout - A hook that automatically sets a timeout when a value changes
 * and cleans up properly. Useful for auto-dismissing messages.
 *
 * @param {Function} callback - Function to call when timeout fires
 * @param {number} delay - Delay in milliseconds
 * @param {any} trigger - Value that triggers the timeout when it becomes truthy
 *
 * @example
 * // Auto-dismiss error message after 5 seconds
 * useAutoTimeout(() => setErrorMessage(null), 5000, errorMessage);
 */
export function useAutoTimeout(callback, delay, trigger) {
  const callbackRef = useRef(callback);

  // Update callback ref when callback changes
  useEffect(() => {
    callbackRef.current = callback;
  }, [callback]);

  useEffect(() => {
    if (!trigger) return;

    const timeoutId = setTimeout(() => {
      callbackRef.current();
    }, delay);

    return () => clearTimeout(timeoutId);
  }, [trigger, delay]);
}

export default useSafeTimeout;
